import { Injectable } from '@angular/core';
import { BootstrapThemeProp, BootstrapThemeService } from '@app/core/services/bootstrap-theme.service';
import { TOAST_DEFAULT_TIMEOUT } from '@app/modules/toasts/models/constants/toast.constants';
import { ToastConfig, ToastOptions, ToastPosition } from '@app/modules/toasts/models/interfaces/toast.interfaces';
import { IconDefinition, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ToastService {

    toastId: number = 0;
    readonly infoIcon: IconDefinition = faInfoCircle;

    private readonly _toastPosition: BehaviorSubject<ToastPosition> = new BehaviorSubject<ToastPosition>('top-end');
    toastPosition$: Observable<ToastPosition> = this._toastPosition.asObservable();

    private readonly _toasts: BehaviorSubject<ToastConfig[]> = new BehaviorSubject<ToastConfig[]>([]);
    toasts$: Observable<ToastConfig[]> = this._toasts.asObservable();

    constructor(
        private readonly bootstrapThemeService: BootstrapThemeService
    ) { }

    private showToast(
        theme: BootstrapThemeProp,
        config: string | ToastOptions,
        dismissOtherToasts: boolean = false): number {

        if (dismissOtherToasts) {
            this.dismissAll();
        }

        let toast: ToastConfig;

        if (typeof config === 'string') {
            let icon: IconDefinition;
            if (theme === 'primary') {
                icon = this.infoIcon;
            } else {
                icon = this.bootstrapThemeService.getThemeType(theme).icon;
            }

            toast = {
                id: this.toastId++,
                theme: theme,
                title: '',
                content: config,
                contentIsHtml: false,
                icon: icon,
                timeout: TOAST_DEFAULT_TIMEOUT,
                dismissOnTimeout: true
            };

        } else {
            let icon: IconDefinition;
            if (config.icon) {
                icon = config.icon;
            } else if (theme === 'primary') {
                icon = this.infoIcon;
            } else {
                icon = this.bootstrapThemeService.getThemeType(theme).icon;
            }

            toast = {
                id: this.toastId++,
                theme: theme,
                ...config,
                icon: icon
            };
        }

        if (toast.position && toast.position !== this._toastPosition.value) {
            this._toastPosition.next(toast.position);
        }

        this._toasts.value.push(toast);
        this._toasts.next(this._toasts.value);
        return toast.id;
    }

    showToastPrimary(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('primary', config, dismissOtherToasts);
    }

    showToastSecondary(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('secondary', config, dismissOtherToasts);
    }

    showToastSuccess(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('success', config, dismissOtherToasts);
    }

    showToastError(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('danger', config, dismissOtherToasts);
    }

    showToastWarning(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('warning', config, dismissOtherToasts);
    }

    showToastInfo(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('info', config, dismissOtherToasts);
    }

    showToastDiscovery(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('discovery', config, dismissOtherToasts);
    }

    showToastDownloading(config: string | ToastOptions, dismissOtherToasts: boolean = false): number {
        return this.showToast('downloading', config, dismissOtherToasts);
    }

    dismiss(id: number): void {
        this._toasts.next(this._toasts.value.filter((t) => t.id !== id));
    }

    dismissAll(): void {
        this._toasts.next([]);
    }
}
